import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'

export default function TeoriUndervisning() {
  useEffect(() => {
    document.title = 'Teori & Undervisning - Borgs Køreskole I/S'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Nærværende og tryg teoriundervisning hos Borgs Køreskole I/S i Kalundborg. Interaktiv tavle- og pc-undervisning, fleksible hold og høj beståelsesprocent til teoriprøven.'
      )
    }
  }, [])

  return (
    <div className="bg-slate-50 min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="font-header text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Teoriundervisning i Kalundborg
          </h1>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            Hos Borgs Køreskole I/S er teoriundervisningen levende, interaktiv og nærværende. Vi gennemgår alle færdselsregler i et roligt tempo, hvor der er plads til alle dine spørgsmål.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white p-8 border border-slate-200 shadow-sm space-y-4">
            <i className="fa-solid fa-chalkboard-user text-emerald-600 text-3xl block" aria-hidden="true"></i>
            <h3 className="font-header text-xl font-bold text-slate-900">
              Klasseundervisning &amp; Dialog
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Vi benytter moderne tavleundervisning kombineret med åben dialog. Du sidder ikke bare foran en skærm alene – instruktøren sikrer, at alle forstår teorien.
            </p>
          </div>

          <div className="bg-white p-8 border border-slate-200 shadow-sm space-y-4">
            <i className="fa-solid fa-laptop-code text-emerald-600 text-3xl block" aria-hidden="true"></i>
            <h3 className="font-header text-xl font-bold text-slate-900">
              Online Teoritest Adgang
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Som elev hos os får du fri adgang til online teoriprøver, så du kan øve dig derhjemme på din telefon, tablet eller computer.
            </p>
          </div>

          <div className="bg-white p-8 border border-slate-200 shadow-sm space-y-4">
            <i className="fa-solid fa-calendar-days text-emerald-600 text-3xl block" aria-hidden="true"></i>
            <h3 className="font-header text-xl font-bold text-slate-900">
              Fleksibel Holdstart
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Nye teorihold starter løbende. Undervisningen ligger typisk om eftermiddagen/aftenen, så det passer perfekt med skole eller arbejde.
            </p>
          </div>
        </div>

        {/* Undervisning Section (Verbatim from old site) */}
        <div className="bg-white p-8 sm:p-12 border border-slate-200 shadow-sm mb-16 space-y-6">
          <div>
            <h2 className="font-header text-3xl font-extrabold text-slate-900">
              Undervisning
            </h2>
            <p className="mt-2 text-slate-800 font-bold text-base">
              Der er normalt holdstart hver 5. uge, skiftevis mandag og onsdag. Du kan se næste holdstart på forsiden.
            </p>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Der starter normalt nyt hold hver 5. uge, dvs. at hvis du af en eller anden årsag bliver nødt til at stoppe, eller holde pause kan du komme videre med din køreuddannelse inden for 5 uger.
            </p>

            <p>
              Du skal dog være opmærksom på at du kun i få tilfælde kan holde mere end 3 måneders pause uden at skulle starte forfra.
            </p>

            <section className="pt-4 border-t border-slate-200 space-y-2">
              <h3 className="font-header text-xl font-bold text-slate-900">
                Teorilektionerne foregår som tavleundervisning eller pc-undervisning. Dette er valgfrit. Se mere under Tilmelding &gt; Opstart.
              </h3>
              <p>
                Der altid mulighed for at spørge ind til det du ikke forstår undervejs og du får altid muligheden for at øve teoriprøver der hjemme på hjemmeadgangskoder.
              </p>
              <p>
                Før / efter den obligatoriske undervisning, på skolen, er det muligt at tage teoriprøver på storskærm.
              </p>
              <p>
                Det er en fordel fordi at du får rettet prøven i fællesskab, og på den måde for en mere uddybende forklaring, samtidig med at man kan lære af andres fejl, således at man ikke selv begår de samme fejl, til den rigtige teoriprøve.
              </p>
            </section>

            <section className="pt-4 border-t border-slate-200 space-y-2">
              <h3 className="font-header text-xl font-bold text-slate-900">
                Undervisningen i skolevognen foregår efter individuelle aftaler.
              </h3>
              <p>
                Når der køres mørke kørsel afhentes du hjemme hos dig selv og sættes ligeledes af hjemme (med mindre du ønsker andet).
              </p>
              <p>
                Manøvrebane foregår på Rynkevangen 11 her i Kalundborg.
              </p>
              <p>
                Køreteknik foregår i Sorø.
              </p>
              <p>
                Dato er efter nærmere aftale med eleven, kørelæreren og manøvregården / Køreteknisk anlæg.
              </p>
            </section>
          </div>
        </div>

        {/* Theory Location Section */}
        <div className="bg-white p-8 sm:p-12 border border-slate-200 shadow-sm mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <h2 className="font-header text-3xl font-bold text-slate-900">
                Elmegade 19, 4400 Kalundborg
              </h2>
              <p className="text-slate-600 text-sm leading-relaxed">
                Teorilokalet er udstyret med behagelige siddepladser, storskærm og moderne præsentationsudstyr. Vi sørger for kaffe, te og en god atmosfære til hver samling.
              </p>
              <div className="pt-2 flex flex-wrap gap-3">
                <a
                  href="tel:21470187"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition-colors whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">21 47 01 87</span>
                </a>
                <a
                  href="tel:23200425"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-700 text-white font-bold text-sm hover:bg-emerald-800 transition-colors whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">23 20 04 25</span>
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] text-slate-950 font-bold text-sm transition-colors whitespace-nowrap"
                >
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                  <span>Tilmelding til næste hold</span>
                </Link>
              </div>
            </div>

            <div className="relative overflow-hidden shadow-lg border border-slate-200 aspect-video rounded-lg group">
              <iframe
                title="Google Maps overblik - Borgs Køreskole, Elmegade 19, 4400 Kalundborg"
                src="https://maps.google.com/maps?q=Elmegade%2019,%204400%20Kalundborg&t=&z=16&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full border-0 min-h-[280px]"
                allowFullScreen=""
                loading="lazy"
              ></iframe>
              <div className="absolute bottom-3 left-3 bg-slate-900/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-2 shadow-md">
                <i className="fa-solid fa-location-dot text-emerald-400" aria-hidden="true"></i>
                <span>Elmegade 19, 4400 Kalundborg</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
